import ExcelJS from 'exceljs';
import { GrainKey, GroupedRow, PagerColumn, SparcCategory, getGrainLabel } from '../constants/onePagerConstants';
import { isMetricBad } from './onePagerStatus';

export interface BuildWorkbookParams {
	sheetName: string;
	groupedRows: GroupedRow[];
	grandTotal: GroupedRow | null;
	columns: PagerColumn[];
	grainSelections: GrainKey[];
}

const HEADER_AND_TOTAL_FONT_STYLE = {
	font: {
		bold: true,
		sz: 14,
	},
};

const GRAND_TOTAL_FILL_HEX = 'F3F7FD';
const GRAND_TOTAL_FONT_COLOR_HEX = '003A7D';

/** KPI value font colours, mirroring the on-screen table (black default, red when off target) */
const METRIC_DEFAULT_FONT_HEX = '2D2D2D';
const METRIC_BAD_FONT_HEX = 'C12C01';

/** First sheet row (1-based) holding real data: SPARC header, KPI header, grand total, then data */
const FIRST_DATA_SHEET_ROW = 4;
const KPI_HEADER_FILL_HEX = 'F8FAFD';
const SPARC_HEADER_ROW_INDEX = 0;
const COLUMN_HEADER_ROW_INDEX = 1;
const GRAND_TOTAL_ROW_INDEX = 2;

const toWorksheetCellFormat = (col: PagerColumn): string => {
	if (col.fmt.startsWith('pct')) return '0.00%';
	if (col.fmt.startsWith('usd')) return '$#,##0.00';
	return '0.00';
};

const buildSparcSpans = (columns: PagerColumn[]): Array<{ sparc: SparcCategory; start: number; end: number }> => {
	const spans: Array<{ sparc: SparcCategory; start: number; end: number }> = [];
	if (columns.length === 0) return spans;

	let current = columns[0].sparc;
	let start = 0;
	for (let i = 1; i < columns.length; i++) {
		if (columns[i].sparc !== current) {
			spans.push({ sparc: current, start, end: i - 1 });
			current = columns[i].sparc;
			start = i;
		}
	}
	spans.push({ sparc: current, start, end: columns.length - 1 });
	return spans;
};

const sparcLabel = (sparc: SparcCategory): string => {
	switch (sparc) {
		case 'S': return 'Safety';
		case 'P': return 'People';
		case 'A': return 'Accuracy';
		case 'R': return 'Reliability';
		case 'C': return 'Cost';
		case 'N': return 'Navigator';
		default: return sparc;
	}
};

const sparcHeaderFillHex = (sparc: SparcCategory): string => {
	switch (sparc) {
		case 'S': return 'FFE7E1'; // Safety
		case 'P': return 'ECE7FF'; // People
		case 'A': return 'E8F1FD'; // Accuracy
		case 'R': return 'DFF4FA'; // Reliability
		case 'C': return 'E2F8E0'; // Cost
		case 'N': return 'C6DCFB'; // Navigator
		default: return 'FFFFFF';
	}
};

const sparcHeaderTextColorHex = (sparc: SparcCategory): string => {
	switch (sparc) {
		case 'S': return 'C43A17'; // Safety
		case 'P': return '5B48C5'; // People
		case 'A': return '0B5FC6'; // Accuracy
		case 'R': return '19759A'; // Reliability
		case 'C': return '0D7A39'; // Cost
		case 'N': return '01346F'; // Navigator
		default: return '4B4B4B';
	}
};

const NUMERIC_GRAIN_KEYS: GrainKey[] = ['yr', 'pd', 'wk'];

const isNumericGrainKey = (grain: GrainKey): boolean => NUMERIC_GRAIN_KEYS.includes(grain);

const normalizeGrainValueForExcel = (grain: GrainKey, value: string | undefined): string | number => {
	if (value === undefined || value === null || value === '') {
		return value ?? '';
	}
	if (!isNumericGrainKey(grain)) {
		return value;
	}
	const numericValue = Number(value);
	return Number.isFinite(numericValue)
		? numericValue
		: value;
};

const appendDataRow = (
	rows: Array<Array<string | number | null>>,
	row: GroupedRow,
	grainSelections: GrainKey[],
	columns: PagerColumn[]
) => {
	const grainValues = grainSelections.map((grain) => normalizeGrainValueForExcel(grain, row.grainVals[grain]));
	const metricValues = columns.map((col) => {
		const value = row.vals[col.id];
		if (col.id === 'gap' && (value === null || value === undefined)) return '--';
		return typeof value === 'number' && Number.isFinite(value) ? value : null;
	});
	rows.push([...grainValues, ...metricValues]);
};

export const buildWorkbook = ({
	sheetName,
	groupedRows,
	grandTotal,
	columns,
	grainSelections,
}: BuildWorkbookParams): ExcelJS.Workbook => {
	const grainCount = grainSelections.length;
	const totalCols = grainCount + columns.length;
	const frozenColumns = grainSelections.length;
	const frozenHeaderRows = grandTotal ? (GRAND_TOTAL_ROW_INDEX + 1) : (COLUMN_HEADER_ROW_INDEX + 1);
	const workbook = new ExcelJS.Workbook();
	const ws = workbook.addWorksheet(sheetName, {
		views: [{ state: 'frozen', xSplit: frozenColumns, ySplit: frozenHeaderRows }],
	});

	const headerRow1: Array<string | number | null> = Array(totalCols).fill('');
	const headerRow2: Array<string | number | null> = Array(totalCols).fill('');

	grainSelections.forEach((grain, idx) => {
		headerRow1[idx] = getGrainLabel(grain);
	});
	columns.forEach((col, idx) => {
		headerRow2[grainCount + idx] = col.label;
	});

	const rows: Array<Array<string | number | null>> = [headerRow1, headerRow2];

	if (grandTotal) {
		const totalRow: GroupedRow = {
			...grandTotal,
			grainVals: {
				...grandTotal.grainVals,
				[grainSelections[0] ?? '_total']: 'Grand Total',
			},
		};
		appendDataRow(rows, totalRow, grainSelections, columns);
	} else {
		rows.push(Array(totalCols).fill(''));
	}

	groupedRows.forEach((row) => appendDataRow(rows, row, grainSelections, columns));

	rows.forEach((row) => {
		ws.addRow(row);
	});

	for (let i = 0; i < grainCount; i++) {
		ws.mergeCells(SPARC_HEADER_ROW_INDEX + 1, i + 1, COLUMN_HEADER_ROW_INDEX + 1, i + 1);
	}

	const sparcSpans = buildSparcSpans(columns);
	sparcSpans.forEach((span) => {
		const startCol = grainCount + span.start + 1;
		const endCol = grainCount + span.end + 1;
		const fillColor = sparcHeaderFillHex(span.sparc);
		const textColor = sparcHeaderTextColorHex(span.sparc);
		const startCell = ws.getCell(SPARC_HEADER_ROW_INDEX + 1, startCol);
		startCell.value = sparcLabel(span.sparc);
		ws.mergeCells(SPARC_HEADER_ROW_INDEX + 1, startCol, SPARC_HEADER_ROW_INDEX + 1, endCol);
		for (let col = startCol; col <= endCol; col++) {
			const cell = ws.getCell(SPARC_HEADER_ROW_INDEX + 1, col);
			cell.fill = {
				type: 'pattern',
				pattern: 'solid',
				fgColor: { argb: `FF${fillColor}` },
			};
			cell.font = {
				bold: true,
				size: 14,
				color: { argb: `FF${textColor}` },
			};
		}
	});

	ws.columns = [
		...grainSelections.map(() => ({ width: 18 })),
		...columns.map(() => ({ width: 14 })),
	];

	const applyRowStyle = (rowIndex: number, fillHex?: string, fontColorHex?: string) => {
		for (let colIndex = 1; colIndex <= totalCols; colIndex++) {
			const cell = ws.getCell(rowIndex + 1, colIndex);
			cell.font = {
				...(cell.font || {}),
				bold: HEADER_AND_TOTAL_FONT_STYLE.font.bold,
				size: HEADER_AND_TOTAL_FONT_STYLE.font.sz,
				...(fontColorHex ? { color: { argb: `FF${fontColorHex}` } } : {}),
			};
			if (fillHex) {
				cell.fill = {
					type: 'pattern',
					pattern: 'solid',
					fgColor: { argb: `FF${fillHex}` },
				};
			}
		}
	};

	const applyKpiHeaderFill = () => {
		for (let colIndex = grainCount + 1; colIndex <= totalCols; colIndex++) {
			ws.getCell(COLUMN_HEADER_ROW_INDEX + 1, colIndex).fill = {
				type: 'pattern',
				pattern: 'solid',
				fgColor: { argb: `FF${KPI_HEADER_FILL_HEX}` },
			};
		}
	};

	const applyNonGrainCenterAlignment = () => {
		const nonGrainStartCol = grainCount + 1;
		for (let rowIndex = 1; rowIndex <= rows.length; rowIndex++) {
			for (let colIndex = nonGrainStartCol; colIndex <= totalCols; colIndex++) {
				const cell = ws.getCell(rowIndex, colIndex);
				cell.alignment = {
					...(cell.alignment || {}),
					horizontal: 'center',
					vertical: 'middle',
				};
			}
		}
	};

	const applyBodyTextFontSize = () => {
		const firstBodyRow = grandTotal ? (GRAND_TOTAL_ROW_INDEX + 2) : (COLUMN_HEADER_ROW_INDEX + 2);
		for (let rowIndex = firstBodyRow; rowIndex <= rows.length; rowIndex++) {
			for (let colIndex = 1; colIndex <= totalCols; colIndex++) {
				const cell = ws.getCell(rowIndex, colIndex);
				cell.font = {
					...(cell.font || {}),
					size: 12,
				};
			}
		}
	};

	applyRowStyle(SPARC_HEADER_ROW_INDEX);
	applyRowStyle(COLUMN_HEADER_ROW_INDEX);
	applyKpiHeaderFill();
	applyNonGrainCenterAlignment();
	if (grandTotal) {
		applyRowStyle(GRAND_TOTAL_ROW_INDEX, GRAND_TOTAL_FILL_HEX, GRAND_TOTAL_FONT_COLOR_HEX);
	}
	applyBodyTextFontSize();

	/**
	 * Colour KPI values to match the on-screen table: red when the API flags the
	 * metric as not meeting target, otherwise black. Runs after the header/total
	 * and body font passes so it wins over their blanket colours — the grand
	 * total keeps its bold weight but gets the same red/black treatment as the
	 * data rows instead of being uniformly blue.
	 */
	const applyMetricStatusColors = () => {
		const paintRow = (sheetRowIndex: number, row: GroupedRow, isTotal: boolean) => {
			for (let colIndex = 0; colIndex < columns.length; colIndex++) {
				const column = columns[colIndex];
				const cell = ws.getCell(sheetRowIndex, grainCount + colIndex + 1);
				const bad = isMetricBad(row, column.id);
				cell.font = {
					...(cell.font || {}),
					bold: isTotal ? true : cell.font?.bold,
					color: {
						argb: `FF${bad ? METRIC_BAD_FONT_HEX : METRIC_DEFAULT_FONT_HEX}`,
					},
				};
			}
		};

		if (grandTotal) {
			paintRow(GRAND_TOTAL_ROW_INDEX + 1, grandTotal, true);
		}
		groupedRows.forEach((row, idx) => {
			paintRow(FIRST_DATA_SHEET_ROW + idx, row, false);
		});
	};

	applyMetricStatusColors();

	const firstDataRow = 2;
	for (let rowIndex = firstDataRow; rowIndex < rows.length; rowIndex++) {
		for (let grainColIndex = 0; grainColIndex < grainSelections.length; grainColIndex++) {
			const grainKey = grainSelections[grainColIndex];
			if (!isNumericGrainKey(grainKey)) continue;
			const grainCell = ws.getCell(rowIndex + 1, grainColIndex + 1);
			if (typeof grainCell.value === 'number') {
				grainCell.numFmt = '0';
			}
		}

		for (let colIndex = 0; colIndex < columns.length; colIndex++) {
			const column = columns[colIndex];
			const absCol = grainCount + colIndex + 1;
			const cell = ws.getCell(rowIndex + 1, absCol);
			if (cell.value === null || cell.value === undefined || cell.value === '') continue;
			if (typeof cell.value === 'number') {
				cell.numFmt = toWorksheetCellFormat(column);
			}
		}
	}

	return workbook;
};
